import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  Stack,
  IconButton,
  Alert
} from '@mui/material';
import { Button } from '@mui/material';
import { Search as SearchIcon, Instagram as InstagramIcon, Facebook as FacebookIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

type Contact = {
  _id: string;
  userId: string;
  platform: 'instagram' | 'facebook' | 'whatsapp';
  contactId: string;
  name?: string;
  username?: string;
  profilePicture?: string;
  lastMessage?: string;
  lastMessageAt?: string;
  messageCount?: number;
};

type ContactsResponse = {
  contacts: Contact[];
  pagination: { page: number; limit: number; total: number; pages: number };
};

// WhatsApp Icon Component (same style as Messages page)
const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: 16, height: 16, color: '#25D366' }}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
  </svg>
);

const Contacts: React.FC = () => {
  const { user } = useAuth();
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;

  const fetchContacts = useMemo(() => {
    return async (pageParam: number, searchParam: string) => {
      const res = await axios.get<ContactsResponse>('/api/contacts', {
        params: { page: pageParam, limit, search: searchParam },
        withCredentials: true,
      });
      const data = res.data;
      setContacts(data.contacts || []);
      setPages(data.pagination?.pages || 1);
      setTotal(data.pagination?.total || 0);
    };
  }, []);

  useEffect(() => {
    fetchContacts(page, search).catch(() => {});
  }, [page, search, fetchContacts]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPage(1);
    setSearch(e.target.value);
  };

  const noConnectedAccounts = !user?.facebookPageId && !user?.instagramBusinessId && !user?.whatsappBusinessAccountId && !user?.tiktokId;

  return (
    <Box sx={{ position: 'relative' }}>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
            <Typography variant="body1" color="text.secondary">
              View contacts from conversations. Anyone you’ve messaged at least once is saved automatically.
            </Typography>
            <TextField
              placeholder="Search contacts..."
              value={search}
              onChange={handleSearchChange}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
          </Box>
          <Typography variant="body2" color="text.secondary" mt={1}>
            {total} total contacts
          </Typography>
        </CardContent>
      </Card>

      <Box sx={{ ...(noConnectedAccounts ? { filter: 'blur(1.2px)', pointerEvents: 'none', userSelect: 'none' } : {}) }}>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Contact</TableCell>
              <TableCell>Platform</TableCell>
              <TableCell>Last Message</TableCell>
              <TableCell>Last Seen</TableCell>
              <TableCell align="right">Messages</TableCell>
              <TableCell align="right" width={80}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {contacts.map((c) => (
              <TableRow key={c._id} hover>
                <TableCell>
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Avatar src={c.profilePicture} alt={c.name || c.username || c.contactId} />
                    <Box>
                      <Typography fontWeight={600}>
                        {c.name || c.username || c.contactId}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {c.contactId}
                      </Typography>
                    </Box>
                  </Stack>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {c.platform === 'facebook' ? (
                      <FacebookIcon sx={{ fontSize: 18, color: '#1877F2' }} />
                    ) : c.platform === 'instagram' ? (
                      <InstagramIcon sx={{ fontSize: 18, color: '#E1306C' }} />
                    ) : (
                      <WhatsAppIcon />
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography noWrap maxWidth={360}>{c.lastMessage || '-'}</Typography>
                </TableCell>
                <TableCell>
                  {c.lastMessageAt ? new Date(c.lastMessageAt).toLocaleString() : '-'}
                </TableCell>
                <TableCell align="right">{c.messageCount ?? '-'}</TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={async () => {
                      try {
                        await axios.delete(`/api/contacts/${c._id}`, { withCredentials: true });
                        // Refresh current page
                        fetchContacts(page, search);
                      } catch (e) {
                        // no-op
                      }
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {contacts.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center" style={{ padding: 24 }}>
                  <Typography color="text.secondary">No contacts found.</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {pages > 1 && (
        <Box display="flex" justifyContent="center" mt={2}>
          <Pagination page={page} count={pages} onChange={(_, p) => setPage(p)} color="primary" />
        </Box>
      )}
      </Box>

      {noConnectedAccounts && (
        <Box sx={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 1300,
          pointerEvents: 'auto',
          userSelect: 'auto',
          width: '100%',
          maxWidth: 520
        }}>
          <Alert severity="warning" sx={{
            backgroundColor: '#FFF7E6',
            border: '1px solid #FFE1A3',
            color: '#8A6D3B',
            width: '100%',
            textAlign: 'center',
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, textAlign: 'center' }}>
              No Connected Accounts
            </Typography>
            <Typography sx={{ mb: 3 }}>
              Connect at least one social media account to view and manage your contacts.
            </Typography>
            <Button
              variant="contained"
              color="warning"
              onClick={() => window.dispatchEvent(new Event('open-connect-dropdown'))}
              sx={{ fontWeight: 600 }}
            >
              Connect Account
            </Button>
          </Alert>
        </Box>
      )}
    </Box>
  );
};

export default Contacts;


